/**
 * CORE DE EXPORTAÇÃO - Funções Utilitárias Compartilhadas
 * 
 * Este arquivo contém todas as funções e configurações centrais 
 * usadas pelos diferentes formatos de exportação (PDF, HTML, Excel, CSV).
 * 
 * FUNÇÕES PRINCIPAIS:
 * - formatAppliedFilters: Formata filtros aplicados para exibição
 * - preparePdfContent: Prepara conteúdo HTML para conversão em PDF
 * - loadImageAsDataURL: Converte imagens para base64
 * - renderFAIconToDataURL: Renderiza ícones FontAwesome como imagem
 * - getExportColumns: Obtém colunas configuradas para exportação
 * - formatDataForExport: Formata dados da grid para exportação
 * 
 * INICIALIZAÇÕES:
 * - FontAwesome: Carrega todos os ícones (fas, far, fab)
 * - html2canvas: Disponibiliza globalmente para jsPDF
 * - IMAGE_SPECS: Configurações padrão para imagens em PDFs
 * 
 * USADO POR: LayoutPdf.js, LayoutPdfHtml.js, LayoutHtml.js, LayoutExcel.js, LayoutCsv.js
 * 
 * IMPORTANTE: NÃO REMOVER - Este arquivo é essencial para todas as exportações!
 */

import { library, icon } from '@fortawesome/fontawesome-svg-core'
import * as fas from '@fortawesome/free-solid-svg-icons'
import * as far from '@fortawesome/free-regular-svg-icons'
import * as fab from '@fortawesome/free-brands-svg-icons'
import { Canvg } from 'canvg'
import html2canvas from 'html2canvas'
import logoDefender from '../../../../assets/images/logo-defender-white-full.svg'
import logoBusiness from '../../../../assets/images/logo-business-white.svg'

try {
  library.add(
    ...(Object.values(fas).filter((i) => i && i.iconName)),
    ...(Object.values(far).filter((i) => i && i.iconName)),
    ...(Object.values(fab).filter((i) => i && i.iconName))
  )
} catch (_) {}

try {
  if (typeof window !== 'undefined') {
    // @ts-ignore
    window.html2canvas = window.html2canvas || html2canvas
  }
} catch (_) {}

export const IMAGE_SPECS = {
  IMAGE_SIZE: 11,
  IMAGE_COL_WIDTH: 14,
  IMAGE_MIN_ROW_HEIGHT: 14,
}

export const loadImageAsDataURL = (src) => {
  return new Promise((resolve) => {
    if (!src || typeof src !== 'string') return resolve(null);
    if (src.startsWith('data:image')) return resolve(src);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = function () { resolve(null); };
    img.src = src;
  });
};

export const resolveColorFromClass = (cls) => {
  const s = (cls || '').toLowerCase();
  const rgb = s.includes('vermelho') || s.includes('danger') || s.includes('times') || s.includes('ban')
    ? [220, 53, 69]
    : s.includes('verde') || s.includes('success') || s.includes('check')
    ? [40, 167, 69]
    : s.includes('amarelo') || s.includes('warning') || s.includes('exclamation')
    ? [255, 193, 7]
    : s.includes('azul') || s.includes('primary') || s.includes('info')
    ? [0, 123, 255]
    : s.includes('cinza') || s.includes('secondary') || s.includes('gray')
    ? [108, 117, 125]
    : s.includes('preto') || s.includes('dark')
    ? [33, 37, 41]
    : s.includes('laranja')
    ? [253, 126, 20]
    : [108, 117, 125];
  return { rgb, css: `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})` };
};

export const renderFAIconToDataURL = async (faClass, sizePx = 64, color = '#6c757d') => {
  try {
    if (!faClass) return null;
    const parts = faClass.split(/\s+/).filter(Boolean);
    const style = parts.find((p) => p === 'fas' || p === 'far' || p === 'fab') || 'fas';
    const nameClass = parts.find((p) => p.startsWith('fa-')) || '';
    if (!nameClass) return null;
    const name = nameClass.replace('fa-', '');
    const faIcon = icon({ prefix: style, iconName: name }, { classes: [], styles: { color } });
    if (!faIcon || !faIcon.html || !faIcon.html[0]) return null;
    const svg = faIcon.html[0];
    const canvas = document.createElement('canvas');
    canvas.width = sizePx;
    canvas.height = sizePx;
    const ctx = canvas.getContext('2d');
    const v = await Canvg.from(ctx, svg);
    await v.render();
    return canvas.toDataURL('image/png');
  } catch (e) {
    return null;
  }
};

export const preparePdfContent = async (dataResult, exportColumns) => {
  const imageMap = new Map();
  const imageColumnIndexes = new Set();
  const tableData = await Promise.all(
    dataResult.map(async (row, rowIdx) => {
      return Promise.all(
        exportColumns.map(async (col, colIdx) => {
          let value = row[col.field] || '';

          const hasImageFlag = (col.hasImage === 'True' || col.hasImage === true || col.HasImage === true);
          if (hasImageFlag) {
            const imageSrcCandidates = [];
            const iconClassCandidates = [];
            if (typeof value === 'string') {
              const trimmed = value.trim();
              if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
                try {
                  const parsed = JSON.parse(trimmed);
                  if (Array.isArray(parsed)) {
                    parsed.forEach((it) => {
                      if (it?.tipo === 'image' && it?.valor) imageSrcCandidates.push(it.valor);
                      if (it?.tipo === 'icone' && it?.valor) iconClassCandidates.push(it.valor);
                    });
                  } else {
                    if (parsed?.tipo === 'image' && parsed?.valor) imageSrcCandidates.push(parsed.valor);
                    if (parsed?.tipo === 'icone' && parsed?.valor) iconClassCandidates.push(parsed.valor);
                    if (!parsed?.tipo && parsed?.valor) imageSrcCandidates.push(parsed.valor);
                  }
                } catch (_) {
                  imageSrcCandidates.push(trimmed);
                }
              } else {
                imageSrcCandidates.push(trimmed);
              }
            }

            const cellKey = `${rowIdx}-${colIdx}`;
            const dataUrls = [];
            for (const src of imageSrcCandidates) {
              const du = await loadImageAsDataURL(src);
              if (du) dataUrls.push(du);
            }
            for (const icn of iconClassCandidates) {
              const { css } = resolveColorFromClass(icn);
              const du = await renderFAIconToDataURL(icn, 64, css);
              if (du) dataUrls.push(du);
            }
            if (dataUrls.length) {
              imageMap.set(cellKey, dataUrls);
              imageColumnIndexes.add(colIdx);
            }
            return '';
          } else {
            if (typeof value === 'string') {
              const cleaned = value.replace(/[^\x20-\x7E\n\r\t]/g, '');
              const trimmed = cleaned.trim();
              if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
                try {
                  const parsed = JSON.parse(trimmed);
                  const iconClasses = [];
                  if (Array.isArray(parsed)) {
                    parsed.forEach((it) => { if (it?.tipo === 'icone' && it?.valor) iconClasses.push(it.valor); });
                  } else if (parsed?.tipo === 'icone' && parsed?.valor) {
                    iconClasses.push(parsed.valor);
                  }
                  if (iconClasses.length) {
                    const urls = [];
                    for (const icn of iconClasses) {
                      const { css } = resolveColorFromClass(icn);
                      const du = await renderFAIconToDataURL(icn, 64, css);
                      if (du) urls.push(du);
                    }
                    if (urls.length) {
                      imageMap.set(`${rowIdx}-${colIdx}`, urls);
                      imageColumnIndexes.add(colIdx);
                      return '';
                    }
                  }
                } catch (_) {}
              }
              return cleaned.substring(0, 50);
            }
            return String(value).substring(0, 50);
          }
        })
      );
    })
  );

  return { tableData, imageMap, imageColumnIndexes };
}

export const getPdfColumnStyles = (imageColumnIndexes, imageColWidth, exportColumns = []) => {
  const columnStyles = {};
  
  // Aplicar larguras das colunas de imagem
  imageColumnIndexes.forEach((idx) => {
    columnStyles[idx] = { cellWidth: imageColWidth, halign: 'center', valign: 'middle' };
  });
  
  // Aplicar larguras customizadas das colunas se definidas
  exportColumns.forEach((col, idx) => {
    if (col.width && !columnStyles[idx]) {
      let width = col.width;
      if (typeof width === 'string') {
        if (width.includes('px')) {
          // Converter pixels para mm (aproximadamente)
          const pxValue = parseInt(width.replace('px', ''));
          width = Math.max(10, Math.round(pxValue * 0.264583)); // 1px ≈ 0.264583mm
        } else if (width.includes('%')) {
          // Porcentagem: usar largura proporcional da página
          const percentValue = parseInt(width.replace('%', ''));
          width = Math.max(10, Math.round((percentValue / 100) * 200)); // Base de 200mm
        } else if (width.includes('mm')) {
          width = Math.max(10, parseInt(width.replace('mm', '')));
        } else {
          // Se for só número, assumir que já está em mm
          width = Math.max(10, parseInt(width) || 30);
        }
      } else if (typeof width === 'number') {
        width = Math.max(10, width);
      } else {
        width = 30; // valor padrão em mm
      }
      
      columnStyles[idx] = { cellWidth: width, halign: 'left', valign: 'middle' };
    }
  });
  
  return columnStyles;
}

export const getExportColumns = (columns = []) => {
  try {
    return columns.filter(column =>
      (column.Export === "True" || column.Export === true) &&
      column.hasImage !== "True" &&
      column.hasImage !== true
    ).map(column => ({
      field: column.field,
      header: column.header
    }));
  } catch (error) {
    return [];
  }
};

export const formatDataForExport = (data, columns) => {
  if (!data || !columns) return [];
  return data.map(item => {
    const formattedItem = {};
    columns.forEach(column => {
      formattedItem[column.header] = item[column.field] || '';
    });
    return formattedItem;
  });
};

export const filterDataForExport = (data, columns) => {
  if (!data || !columns) return [];
  return data.map(item => {
    const filteredItem = {};
    columns.forEach(column => {
      let value = item[column.field] || '';
      if (typeof value === 'string' && (
        value.includes('"tipo": "icone"') || 
        value.includes('"tipo": "image"') ||
        column.field.toLowerCase().includes('foto') ||
        column.field.toLowerCase().includes('photo') ||
        column.field.toLowerCase().includes('imagem') ||
        column.field.toLowerCase().includes('image') ||
        value.length > 10000
      )) {
        try {
          JSON.parse(value);
          filteredItem[column.field] = value;
        } catch (e) {
          if (value.length > 1000 || value.startsWith('data:image')) {
            filteredItem[column.field] = '📷 Imagem';
          } else if (column.field.toLowerCase().includes('foto') || column.field.toLowerCase().includes('photo')) {
            filteredItem[column.field] = '📷 Foto';
          } else {
            filteredItem[column.field] = value;
          }
        }
      } else {
        if (typeof value === 'string') {
          value = value
            .replace(/[^\x20-\x7F]/g, '')
            .replace(/[Ø=Ü÷]/g, '');
          if (value.length > 32000) {
            value = value.substring(0, 32000) + '... [TRUNCADO]';
          }
        }
        filteredItem[column.field] = value;
      }
    });
    return filteredItem;
  });
};

export const extractTextFromValue = (value) => {
  if (value == null) return ''
  if (typeof value !== 'string') return String(value)
  const trimmed = value.trim()
  if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return value
  try {
    const parsed = JSON.parse(trimmed)
    const arr = Array.isArray(parsed) ? parsed : [parsed]
    const parts = []
    arr.forEach((it) => {
      if (!it) return
      if (it.tipo === 'texto' && it.valor) parts.push(String(it.valor))
      else if ((it.tipo === 'image' || it.tipo === 'icone') && it.descricao) parts.push(String(it.descricao))
    })
    return parts.length ? parts.join(' - ') : value
  } catch (_) {
    return value
  }
}

export const formatAppliedFilters = (appliedFilters) => {
  if (!appliedFilters || !appliedFilters[0] || Object.keys(appliedFilters[0]).length === 0) {
    return null;
  }
  
  const filters = [];
  const filterObj = appliedFilters[0];
  
  Object.keys(filterObj).forEach(key => {
    const filter = filterObj[key];
    
    if (Array.isArray(filter)) {
      // Para filtros de múltipla seleção
      if (filter[0]?.values && filter[0]?.field) {
        const values = filter[0].values.map(v => v.label || v.value).join(', ');
        filters.push(`${filter[0].field}: ${values}`);
      }
    } else if (filter && typeof filter === 'object') {
      // Para filtros simples
      const value = filter.label || filter.value;
      const field = filter.field || key;
      if (value) {
        filters.push(`${field}: ${value}`);
      }
    }
  });
  
  return filters.length > 0 ? filters.join(' | ') : null;
};

export const getPDFExportColumns = (columns = []) => {
  try {
    return columns.filter(column =>
      (column.Export === "True" || column.Export === true)
    ).map(column => ({
      field: column.field,
      header: column.header,
      HasImage: column.hasImage === "True" || column.hasImage === true,
      Export: column.Export
    }));
  } catch (error) {
    return [];
  }
};

export const getAllColumns = (columns = []) => {
  try {
    return columns.map(column => ({
      field: column.field,
      header: column.header
    }));
  } catch (error) {
    return [];
  }
};

/**
 * BLOCOS HTML COMPARTILHADOS ENTRE LayoutHtml.js E LayoutPdfHtml.js
 *
 * Garante que os dois exportadores (HTML/PDF via servidor e PDF via
 * html2canvas) montem exatamente a mesma estrutura de HTML para o
 * cabeçalho, filtros, informações do usuário, cards de contexto/fluxo
 * e a totalização.
 */

export const escapeHtml = (str) => String(str)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

// Extrai e sanitiza o primeiro <img> ou <i> válido de um HTML vindo do grid
export const extractFirstIconOrImageHtml = (html) => {
  try {
    if (typeof document === 'undefined') return '';
    const container = document.createElement('div');
    container.innerHTML = String(html);
    let found = container.querySelector('img, i');
    if (!found && /&lt;\/?(img|i)\b/i.test(String(html))) {
      const decodeWrap = document.createElement('div');
      decodeWrap.innerHTML = String(html);
      const decoded = decodeWrap.textContent || decodeWrap.innerText || '';
      const reparse = document.createElement('div');
      reparse.innerHTML = decoded;
      found = reparse.querySelector('img, i');
    }
    if (!found) return '';
    const tag = found.tagName.toLowerCase();
    if (tag === 'img') {
      const img = document.createElement('img');
      const src = found.getAttribute('src');
      if (!src) return '';
      img.setAttribute('src', src);
      const cls = found.getAttribute('class');
      if (cls) img.setAttribute('class', cls);
      const alt = found.getAttribute('alt') || '';
      if (alt) img.setAttribute('alt', alt);
      return img.outerHTML;
    }
    if (tag === 'i') {
      const iEl = document.createElement('i');
      const cls = found.getAttribute('class');
      if (cls) iEl.setAttribute('class', cls);
      const ariaHidden = found.getAttribute('aria-hidden');
      if (ariaHidden) iEl.setAttribute('aria-hidden', ariaHidden);
      return iEl.outerHTML;
    }
    return '';
  } catch (_) {
    return '';
  }
};

export const renderExportTableCellHtml = (value, col) => {
  if (value == null) return '';
  if (col.hasImage === 'True' || col.hasImage === true) {
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      const colLabel = `${col?.header || ''} ${col?.field || ''}`.toLowerCase();
      const isAvatarColumn = /(foto|photo|avatar|usu[aá]rio|user|perfil|face|imagem.*pessoa)/.test(colLabel);
      const isValidImageSrc = (src) => {
        if (!src && src !== 0) return false;
        const s = String(src).trim();
        if (!s || s === '[]' || s === '{}' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return false;
        if (s.startsWith('data:image')) return true;
        if (/^https?:\/\//i.test(s)) return true;
        if (/^\//.test(s)) return true; // caminho absoluto
        return false;
      };
      const parts = arr.map((it) => {
        if (it && typeof it === 'object' && it.tipo === 'image') {
          const src = it?.valor;
          if (isValidImageSrc(src)) {
            const cls = isAvatarColumn ? 'img-avatar' : 'img-inline';
            return `<img src="${escapeHtml(src)}" class="${cls}"/>`;
          }
          return '';
        }
        if (it && typeof it === 'object' && it.tipo === 'icone' && it?.valor) {
          return `<i class="${escapeHtml(it.valor)}"></i>`;
        }
        if (it && typeof it === 'object' && it.tipo === 'texto' && it?.valor) {
          return escapeHtml(it.valor);
        }
        if (typeof it === 'string' && (/[<](img|i)\b/i.test(it) || /d-flex|user-photo-label|fa-user-circle/i.test(it))) {
          const normalized = extractFirstIconOrImageHtml(it);
          if (normalized) return normalized;
        }
        if (typeof it === 'string' && isValidImageSrc(it)) {
          const cls = isAvatarColumn ? 'img-avatar' : 'img-inline';
          return `<img src="${escapeHtml(it)}" class="${cls}"/>`;
        }
        return '';
      });
      const joined = parts.join('');
      if (joined && joined.trim().length > 0) {
        return `<span class="icon-cell">${joined}</span>`;
      }
      return `<span class="icon-cell"><i class="far fa-user-circle font-size-32 user-photo-label" aria-hidden="true"></i></span>`;
    } catch (_) {
      if (typeof value === 'string' && (/[<](img|i)\b/i.test(value) || /d-flex|user-photo-label|fa-user-circle/i.test(value))) {
        const normalized = extractFirstIconOrImageHtml(value);
        if (normalized) {
          return `<span class="icon-cell">${normalized}</span>`;
        }
      }
      return `<span class="icon-cell"><i class="far fa-user-circle font-size-32 user-photo-label" aria-hidden="true"></i></span>`;
    }
  }
  const str = typeof value === 'string' ? value : String(value);
  const cleaned = str.replace(/[^\x20-\x7E\n\r\t]/g, '');
  const trimmed = cleaned.trim();
  if (/[<](img|i)\b/i.test(trimmed) || /d-flex|user-photo-label|fa-user-circle/i.test(trimmed)) {
    const normalized = extractFirstIconOrImageHtml(trimmed);
    if (normalized) {
      return `<span class="icon-cell">${normalized}</span>`;
    }
  }
  const looksLikeEmail = trimmed.includes('@') && !/\s/.test(trimmed);
  if (looksLikeEmail) {
    return `<span class="nowrap">${escapeHtml(cleaned)}</span>`;
  }
  return escapeHtml(cleaned);
};

// Cabeçalho com logo e título (tarja azul do Defender)
export const buildReportHeaderHtml = ({ title }) => `
  <div class="defender-header px-5 mb-3">
    <div class="defender-logo d-flex gap-5">
      <img src="${window.location.origin + logoBusiness}" alt="Defender" class="defender-logo-img"/>
      <img src="${window.location.origin + logoDefender}" alt="Defender" class="defender-logo-img"/>
    </div>
    <div class="report-title">${escapeHtml(title)}</div>
  </div>`;

// Chips com os filtros aplicados + código da transação
export const buildFiltersBarHtml = ({ appliedFilters, transactionCode }) => {
  const pills = [];
  const filterObj = (appliedFilters && appliedFilters[0]) || {};
  Object.keys(filterObj).forEach((key) => {
    const filter = filterObj[key];
    if (Array.isArray(filter)) {
      if (filter[0]?.values && filter[0]?.field) {
        filter[0].values.forEach((v) => pills.push(String(v.label || v.value)));
      }
    } else if (filter && typeof filter === 'object') {
      const value = filter.label || filter.value;
      if (value) pills.push(String(value));
    }
  });

  return `
    <div class="filters-bar px-5">
      <div class="filters-left">
        ${pills.length > 0 ? `
        <span class="filters-title">Filtros aplicados:</span>
        <span class="filter-list">${pills.map((p) => `<span class="filter-pill">${escapeHtml(p)}</span>`).join('')}</span>
        ` : ''}
      </div>
      <div class="code-display"><b>Código:</b> ${escapeHtml(transactionCode || '')}</div>
    </div>`;
};

// Coleta os logos de empresas exibidos na tela (usados quando userCompaniesLogos não é informado)
export const collectContextCompanyLogos = () => {
  try {
    if (typeof document === 'undefined') return [];
    const nodeList = document.querySelectorAll('.card-context-dynamic .img-card, .card-context-dynamic .img-context-user');
    const srcs = Array.from(nodeList)
      .map((img) => img && img.getAttribute && img.getAttribute('src'))
      .filter((s) => typeof s === 'string' && s.trim().length > 0);
    return Array.from(new Set(srcs));
  } catch (_) { return []; }
};

const resolveLoggedUserInfo = (options) => {
  const getSafe = (obj, keys, fallback = '') => {
    try {
      return keys.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : null), obj) ?? fallback;
    } catch (_) { return fallback; }
  };
  const parseFoto = (foto) => {
    if (!foto) return null;
    if (typeof foto === 'string') {
      if (foto.startsWith('data:image') || foto.startsWith('http')) return foto;
      try {
        const parsed = JSON.parse(foto);
        if (Array.isArray(parsed) && parsed[0]?.valor) return parsed[0].valor;
      } catch (_) {}
    }
    return null;
  };

  let userInfo = options.userInfo || null;
  if (!userInfo && typeof window !== 'undefined') {
    try {
      const raw = window.localStorage.getItem('authUser');
      const obj = raw ? JSON.parse(raw) : {};
      const profile = getSafe(obj, ['resultset_2', 0]) || getSafe(obj, ['resultset_1', 0]) || obj;
      userInfo = {
        nome: profile.Nome_Completo || profile.Nome_Usuario || profile.Apelido || '',
        cargo: profile.Cargo || '',
        departamento: profile.Departamento || '',
        foto: parseFoto(profile.Foto_Usuario) || profile.Foto_Usuario || null,
      };
    } catch (_) {}
  }
  return userInfo;
};

// Faixa com nome/cargo/departamento/empresas do usuário logado
export const buildUserInfoHtml = (options) => {
  const userNameLabel = options?.labels?.userNameLabel || 'Nome do Usuário';
  const departmentLabel = options?.labels?.departmentLabel || 'Departamento';
  const roleLabel = options?.labels?.roleLabel || 'Cargo';

  const userInfo = resolveLoggedUserInfo(options);
  const companiesLogos = Array.isArray(options.userCompaniesLogos) ? options.userCompaniesLogos : collectContextCompanyLogos();
  const companiesHtml = companiesLogos.map((logo) => `<img src="${escapeHtml(String(logo))}" class="company-logo" alt="Empresa"/>`).join('');

  const html = `
    <div class="user-info-grid">
      <div class="user-col user-name">
        <div class="user-avatar">${userInfo?.foto ? `<img src="${escapeHtml(userInfo.foto)}" class="user-avatar-img" alt="${escapeHtml(userInfo?.nome || 'Usuário')}"/>` : `<i class="far fa-user-circle"></i>`}</div>
        <div class="user-name-info">
          <div class="user-label">${escapeHtml(userNameLabel)}</div>
          <div class="user-value">${escapeHtml(userInfo?.nome || '')}</div>
        </div>
      </div>
      <div class="user-col">
        <div class="user-label">${escapeHtml(departmentLabel)}</div>
        <div class="user-value">${escapeHtml(userInfo?.departamento || '')}</div>
      </div>
      <div class="user-col">
        <div class="user-label">${escapeHtml(roleLabel)}</div>
        <div class="user-value">${escapeHtml(userInfo?.cargo || '')}</div>
      </div>
      <div class="user-col">
        <div class="user-label">Empresas</div>
        <div class="user-value companies">${companiesHtml}</div>
      </div>
    </div>`;

  return { html, userInfo };
};

// Barra de totalização de registros
export const buildTotalsBarHtml = (recordCount) => `
  <div class="totals-bar">
    <div class="totals-chip">Totalizadores</div>
    <div class="totals-body">registros <b>${recordCount}</b></div>
  </div>`;

// Renderiza um item dinâmico de contexto (mesmos tipos suportados pelo CardContext)
export const renderContextItemHtml = (item, isUserPhoto = false) => {
  switch (item?.tipo) {
    case 'image': {
      const imageUrl = item?.valor;
      if (!imageUrl) return '';
      const imageClass = isUserPhoto
        ? 'img-context-user d-flex align-items-center bg-transparent'
        : 'img-card font-size-32 d-flex align-items-center bg-transparent';
      return `<img src="${escapeHtml(imageUrl)}" class="${imageClass}" alt="${escapeHtml(item?.descricao || '')}" title="${escapeHtml(item?.descricao || '')}" />`;
    }
    case 'icone':
      return `<i class="${escapeHtml(item?.valor || '')}" aria-hidden="true" title="${escapeHtml(item?.descricao || '')}"></i>`;
    case 'badge':
      return `<span class="${escapeHtml(item?.estilo || '')}" title="${escapeHtml(item?.descricao || '')}">${escapeHtml(item?.valor || '')}</span>`;
    case 'text':
    case 'texto':
      return `<span class="text-color-azul1 font-size-16 font-w-600 card-context-text" title="${escapeHtml(item?.descricao || '')}">${escapeHtml(item?.valor || '')}</span>`;
    default:
      return '<i class="far fa-user-circle" aria-hidden="true" title="Tipo de componente desconhecido ou valor ausente"></i>';
  }
};

// Monta o HTML equivalente ao componente CardContext a partir de options.contextData
export const buildContextCardHtml = (data) => {
  if (!Array.isArray(data) || data.length === 0) return '';

  const itemsHtml = data.map((item) => {
    const titulo = item?.label ?? '';
    const valor = item?.value ?? '';
    const largura = item?.largura ?? 'col';
    let hasObject = false;

    try {
      if (
        typeof item?.value === 'string' &&
        (item.value.startsWith('{') || item.value.startsWith('['))
      ) {
        hasObject = JSON.parse(item.value);
      }
    } catch (_) {}

    if (!titulo && !valor) return '';

    if (hasObject) {
      const innerHtml = Array.isArray(hasObject) && hasObject.length > 0
        ? hasObject.map((innerItem) => renderContextItemHtml(innerItem)).join('')
        : renderContextItemHtml(item);

      return `
        <div class="card-context-dynamic-item d-flex flex-column ${escapeHtml(largura)}">
          <span class="font-size-14 font-w-500 mb-1 text-color-cinza5">${escapeHtml(titulo)}</span>
          <div class="d-flex align-items-center gap-1">${innerHtml}</div>
        </div>`;
    }

    return `
      <div class="card-context-dynamic-item d-flex flex-column ${escapeHtml(largura)}">
        <span class="font-size-14 font-w-400 mb-1 text-color-cinza5">${escapeHtml(titulo)}</span>
        ${valor ? `<span class="text-color-cinza5 font-size-14 font-w-600 card-context-text">${escapeHtml(valor)}</span>` : ''}
      </div>`;
  }).join('');

  return `
    <div class="card-context-dynamic-wrapper d-flex flex-column" style="flex: auto;">
      <div class="card-context-dynamic pdf-preview d-flex justify-content-between align-items-start p-4 flex-wrap">
        ${itemsHtml}
      </div>
    </div>`;
};

// Monta o HTML equivalente ao componente CardContextExpansive a partir de options.contextColapseData
// No export estático não há alternância de expandido/recolhido: todos os campos são exibidos.
export const buildContextColapseHtml = (data, enableQRCode) => {
  if (!Array.isArray(data) || data.length === 0) return { html: '', qrCodeItems: [] };

  const leftItems = Array.isArray(data[0]) ? data[0] : [];
  const rightItems = Array.isArray(data[1]) ? data[1] : [];
  const qrCodeItems = [];
  
  const exibirQRCode = enableQRCode;

  const qrCodeCol = leftItems.map((item) => {
    if (exibirQRCode && item?.qrcode) {
      const qrId = `export-context-qrcode-${qrCodeItems.length}`;
      qrCodeItems.push({ id: qrId, value: item.qrcode });
      return `
        <div class="d-flex flex-column mb-3 align-items-center justify-content-center col-12">
          <div id="${qrId}" class="d-flex justify-content-center align-items-center" style="min-height: 220px;"></div>
        </div>`;
    }
  })
  
  const leftColHtml = leftItems.map((item) => {
    return `
      <div class="d-flex flex-column mb-3 ${escapeHtml(item?.largura || '')}">
        <span class="font-size-14 font-w-400 mb-1 text-color-cinza5 flex-wrap">${escapeHtml(item?.titulo || '')}</span>
        <span class="font-size-14 font-w-600 text-color-cinza5 flex-wrap">${escapeHtml(item?.valor || '')}</span>
      </div>`;
  }).join('');

  const rightColHtml = rightItems.map((item) => {
    return `
      <div class="d-flex flex-column mb-3 ${escapeHtml(item?.largura || '')}">
        ${item?.titulo ? `<span class="font-size-16 font-w-400 mb-1 text-color-cinza5 flex-wrap">${escapeHtml(item.titulo)}</span>` : ''}
        ${item?.subtitulo ? `<span class="font-size-14 font-w-600 mb-1 text-color-cinza5 flex-wrap">${escapeHtml(item.subtitulo)}</span>` : ''}
        ${item?.descricao ? `<span class="font-size-12 font-w-600 text-color-cinza5 flex-wrap">${escapeHtml(item.descricao)}</span>` : ''}
        ${item?.html ? `<div>${item.html}</div>` : ''}
      </div>`;
  }).join('');

  const leftColClass = enableQRCode ? 'col-md-3 col-12' : 'col-md-5 col-12';
  const rightColClass = 'col';
  const qrCodeColClass = 'col-3';

  const html = `
    <div class="card-context-dynamic-wrapper d-flex flex-column cb-variant-bordered">
      <div class="px-4">
        <div class="row gap-3">
          ${leftItems.length > 0 && !exibirQRCode ? `
          <div class="${leftColClass} px-3 card-context-dynamic pdf-preview">
            <div class="row py-3">${leftColHtml}</div>
          </div>` : ''}
          <div class="${rightColClass} px-3 card-context-dynamic pdf-preview ">
            <div class="row py-3">${rightColHtml}</div>
          </div>
          ${exibirQRCode ? `
            <div class="${qrCodeColClass} card-context-dynamic pdf-preview p-3">
              <div class="row">${qrCodeCol}</div>
            </div>
          ` : ''}
        </div>
      </div>
    </div>`;

  return { html, qrCodeItems };
};

// Monta o HTML equivalente ao componente CardFlow a partir de options.flowData
export const buildFlowHtml = (flowData) => {
  if (!Array.isArray(flowData) || flowData.length === 0) return '';

  const formatFlowName = (name = '') => {
    const formatted = name.charAt(0).toUpperCase() + name.toLowerCase().slice(1);
    return name.length > 11 ? `${formatted.slice(0, 11)}...` : formatted;
  };

  const itemsHtml = flowData.map((item, index) => {
    const name = item?.name ?? '';
    const description = item?.description ?? '';
    const logo = item?.logo ?? null;

    const logoHtml = logo
      ? `<div class="card-flow-logo-wrapper"><img src="${escapeHtml(logo)}" alt="${escapeHtml(name)}" class="card-flow-logo" /></div>`
      : '<i class="far fa-user-circle user-photo-label"></i>';

    const arrowHtml = index < flowData.length - 1
      ? '<i class="bx bx-right-arrow-alt card-flow-arrow"></i>'
      : '';

    return `
      <div class="d-flex align-items-center gap-2">
        ${logoHtml}
        <div class="flow-item d-flex flex-column" title="${escapeHtml(name)}">
          <span class="card-flow-name" title="${escapeHtml(name)}">${escapeHtml(formatFlowName(name))}</span>
          ${description ? `<span class="card-flow-size text-color-cinza5 font-size-14">${escapeHtml(description)}</span>` : ''}
        </div>
      </div>
      ${arrowHtml}`;
  }).join('');

  return `
    <div class="card-flow-dynamic-container d-flex align-items-center justify-content-center gap-3">
      ${itemsHtml}
    </div>`;
};

// Agrupa flow/context/context-colapse na mesma disposição de grid usada em BI_WS0025
export const buildContextRowHtml = ({ flowHtml, contextCardHtml, contextColapseHtml }) => {
  if (!flowHtml && !contextCardHtml && !contextColapseHtml) return '';
  return `
    <div class="row align-items-center">
      ${flowHtml ? `<div class="mb-2 col-12 col-lg-auto">${flowHtml}</div>` : ''}
      ${contextCardHtml ? `<div class="mb-3 col">${contextCardHtml}</div>` : ''}
      ${contextColapseHtml ? `<div class="col-12 mb-3">${contextColapseHtml}</div>` : ''}
    </div>`;
};

// Script que inicializa os QR Codes do bloco CardContextExpansive (via qrcodejs, já usado pela aplicação)
export const buildContextQrCodeScript = (qrCodeItems) => {
  if (!Array.isArray(qrCodeItems) || qrCodeItems.length === 0) return '';
  return `
    <script src="https://cdn.jsdelivr.net/gh/davidshimjs/qrcodejs/qrcode.min.js"></script>
    <script>
      (function initExportQrCodes() {
        var qrItems = ${JSON.stringify(qrCodeItems).replace(/</g, '\\u003c')};
        function render() {
          qrItems.forEach(function (item) {
            var el = document.getElementById(item.id);
            if (el && window.QRCode) {
              new window.QRCode(el, { text: String(item.value), width: 200, height: 200, correctLevel: window.QRCode.CorrectLevel.H });
            }
          });
        }
        if (document.readyState === 'loading') {
          window.addEventListener('DOMContentLoaded', render);
        } else {
          render();
        }
      })();
    </script>`;
};


